import React from 'react';
import { Link } from 'react-router-dom';

import { format } from 'utils/format';
import {
  ENVIRONMENT_METHODOLOGY_PATH,
  formatCo2,
  formatFactor,
  sumEnvironment,
} from 'utils/environment';

import { Invoice } from 'entities/Invoice';
import { TermInfo } from 'components/term-info/term-info.component';

import {
  Title,
  UCsDigestContainer,
  IconContainer,
  FactoryIcon,
  PlantIcon,
  ItemContainer,
  Equivalence,
  Footnote,
} from './enviroment-results.styles';

interface EnvironmentResultsProps {
  invoiceList: Invoice[];
}

// Pegada de carbono (energia retirada da rede × fator médio do SIN) e emissão
// evitada pela geração solar (geração × margem combinada), lado a lado — nunca
// subtraídas. Valores e fatores vêm prontos do backend (fatores MCTI, out/2026);
// aqui só se soma o período. Ver artigo "Impacto ambiental" na Ajuda.
const EnvironmentResults = ({ invoiceList }: EnvironmentResultsProps) => {
  const totals = sumEnvironment(
    invoiceList.map((item) => item.virtual?.virtualEnvironment)
  );
  const hasAvoided = totals.avoidedKg > 0;
  // Fatores no "i" do título (30/out/2026, pedido do Bruno — antes no rodapé).
  // Out/2026: os números diferiam do relatório sem explicação, daí a frase
  // sobre o fator do mês de cada fatura.
  const factorsNote = totals.factorsAvailable
    ? `Fatores MCTI: ${formatFactor(totals.averageFactor)} kg CO₂/kWh (rede)${
        hasAvoided
          ? ` · ${formatFactor(totals.combinedMarginFactor)} kg CO₂/kWh (evitado)`
          : ''
      } — média do período selecionado; o relatório de cada fatura usa os fatores do mês dela.${
        totals.provisional ? ' Fatores provisórios.' : ''
      }`
    : 'Fatores de emissão do MCTI ainda não carregados.';

  return (
    <UCsDigestContainer className="p-4">
      <Title className="mb-4">
        Resultados ambientais
        <TermInfo text={factorsNote} />
      </Title>
      <ItemContainer className="mb-3">
        <div className="me-3">
          <IconContainer>
            <FactoryIcon />
          </IconContainer>
        </div>
        <div>
          <small className="mb-1">
            Sua pegada de carbono (energia da rede) no período
          </small>
          <p className="mb-0">{formatCo2(totals.emissionKg)} de CO₂</p>
          <Equivalence>
            equivale a {format.number(totals.fallenTrees, 1)} árvores ou{' '}
            {format.energy(totals.petrolLiters)} L de gasolina
          </Equivalence>
        </div>
      </ItemContainer>
      {hasAvoided && (
        <ItemContainer className="mb-3">
          <div className="me-3">
            <IconContainer>
              <PlantIcon />
            </IconContainer>
          </div>
          <div>
            <small className="mb-1">
              Emissão evitada pela sua geração solar no período
            </small>
            <p className="mb-0">{formatCo2(totals.avoidedKg)} de CO₂</p>
            <Equivalence>
              equivale a {format.number(totals.plantedTrees, 1)} árvores
              plantadas ou {format.energy(totals.avoidedPetrolLiters)} L de
              gasolina
            </Equivalence>
          </div>
        </ItemContainer>
      )}
      <Footnote>
        <Link to={ENVIRONMENT_METHODOLOGY_PATH}>Como calculamos</Link>
      </Footnote>
    </UCsDigestContainer>
  );
};

export { EnvironmentResults };
